/**
 * Type in one client — prototype usageModal (docs/prototype.html 3249-3274): add or correct a single client's
 * numbers for one product and month. POST /usage/manual.
 *
 * The client-product choices are the overview's cells (GET /usage/overview, already scoped: a member sees only
 * their own clients). "They bought X a month. Now at Y." and the api-source warning come from GET /usage/clients/{id}.
 * An attached report goes up through the same signed upload as a product report (its key is sent as `file_key`).
 */
import { useMemo, useState } from 'react';
import { ATTACHMENT_EXTENSIONS, UPLOAD_MAX_BYTES } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { currentPeriod, nf, periodLabel, reportPeriods, sendFile, useClientUsage, useManualUsage, useProducts, useUsageOverview } from '../../lib/usage';
import { Empty, ErrorState, Field, Loading, Modal, errorText, useToast } from '../../ui/kit';

export function TypeInModal({ onClose, clientId, productKey }: { onClose: () => void; clientId?: string; productKey?: string }) {
  const ov = useUsageOverview();
  const pr = useProducts();
  const save = useManualUsage();
  const toast = useToast();
  const periods = useMemo(() => reportPeriods(), []);
  const now = currentPeriod();

  const opts = useMemo(() => {
    const names = new Map((ov.data?.products ?? []).map((p) => [p.key, p.name]));
    return (ov.data?.clients ?? []).flatMap(({ client, cells }) =>
      cells.map((c) => ({ v: `${client.id}|${c.product_key}`, client, product_key: c.product_key, label: `${client.name}: ${names.get(c.product_key) ?? c.product_key}` })),
    );
  }, [ov.data]);

  const [sel, setSel] = useState<string | null>(clientId && productKey ? `${clientId}|${productKey}` : null);
  const cur = opts.find((o) => o.v === sel) ?? opts.find((o) => o.client.id === clientId) ?? opts[0];
  const cu = useClientUsage(cur?.client.id ?? null);
  const p = cu.data?.products.find((x) => x.product_key === cur?.product_key);
  const product = pr.data?.find((x) => x.key === cur?.product_key);
  const extras = (product?.fields ?? []).filter((f) => !f.is_main);

  const [period, setPeriod] = useState(periods[0]!);
  const [main, setMain] = useState('');
  const [xs, setXs] = useState<Record<string, string>>({});
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  const pick = (v: string) => {
    setSel(v);
    setMain('');
    setXs({});
    setErr({});
  };

  const submit = async () => {
    if (!cur || !p) return;
    const e: Record<string, string> = {};
    if (main.trim() === '') e.main_value = `Enter the ${p.main_metric.toLowerCase()}`;
    else if (!(Number(main) >= 0)) e.main_value = 'Enter a number, 0 or more';
    if (file && !ATTACHMENT_EXTENSIONS.some((x) => file.name.toLowerCase().endsWith(x))) e.file = 'Attach a PDF, Excel, CSV or screenshot (.pdf, .xlsx, .csv, .tsv, .txt, .png or .jpg).';
    if (file && file.size > UPLOAD_MAX_BYTES) e.file = 'The file is over 10 MB.';
    setErr(e);
    if (Object.keys(e).length) return;
    const extrasBody = Object.fromEntries(Object.entries(xs).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, Number(v)]));
    try {
      setSending(true);
      const file_key = file ? (await sendFile(file, cur.product_key, period, 'attachment')).spaces_key : undefined;
      await save.mutateAsync({
        client_id: cur.client.id,
        product_key: cur.product_key,
        period,
        main_value: Number(main),
        ...(Object.keys(extrasBody).length ? { extras: extrasBody } : {}),
        ...(file_key ? { file_key } : {}),
      });
      toast(`Report saved for ${cur.client.name}. Health updated.`);
      onClose();
    } catch (x) {
      // Zod errors arrive as 'body.<path>', the route's own checks as '<path>' (product_key, extras.<k>, file_key):
      // key them all by the bare path. Anything no field on screen shows goes in the form note, so a 422 is never silent.
      if (!(x instanceof ApiFailure)) return setErr({ form: errorText(x) });
      // The attach's sign refusal (POST /usage-reports/sign: filename / content_type / bytes) belongs under the file input.
      const fe = Object.fromEntries(
        Object.entries(x.fieldErrors).map(([k, v]) => {
          const key = k.replace(/^body\./, '');
          return [['filename', 'content_type', 'bytes'].includes(key) ? 'file' : key, v];
        }),
      );
      const shown = new Set(['period', 'main_value', 'file', 'file_key', ...extras.map((f) => `extras.${f.field_key}`)]);
      const unshown = Object.entries(fe).filter(([k]) => !shown.has(k));
      setErr({ ...fe, form: unshown.length || !Object.keys(fe).length ? errorText(x) : '' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      wide
      title="Type in one client"
      sub="Use this to add or correct a single client’s numbers. To add a whole product report, use Upload usage report."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={() => void submit()} disabled={!p || sending}>
            {sending ? 'Saving…' : 'Save report'}
          </button>
        </>
      }
    >
      {ov.isLoading ? (
        <Loading label="Loading your clients…" />
      ) : ov.error ? (
        <ErrorState error={ov.error} onRetry={() => void ov.refetch()} />
      ) : !cur ? (
        <Empty title="No clients to enter usage for">A client appears here once it has a product.</Empty>
      ) : (
        <>
          <div className="cols c2">
            <Field label="Client and product">
              <select className="in" value={cur.v} onChange={(e) => pick(e.target.value)}>
                {opts.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Report period" error={err.period}>
              <select className="in" value={period} onChange={(e) => setPeriod(e.target.value)}>
                {periods.map((x) => (
                  <option key={x} value={x}>
                    {periodLabel(x, now)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {cu.isLoading ? (
            <Loading label="Loading this client’s usage…" />
          ) : cu.error ? (
            <ErrorState error={cu.error} onRetry={() => void cu.refetch()} />
          ) : !p ? (
            <div className="note warn">This client has not bought that product.</div>
          ) : (
            <>
              <div className="note" style={{ marginBottom: 12 }}>
                They bought{' '}
                <b>
                  {nf(p.plan)} {p.unit}
                </b>{' '}
                a {product?.period ?? 'month'}. Now at <b>{nf(p.used)}</b>.
              </div>
              <label className="fld">
                <span>
                  <b>{p.main_metric}</b> ({p.unit})
                </span>
                <input
                  className="in"
                  type="number"
                  min="0"
                  value={main}
                  onChange={(e) => setMain(e.target.value)}
                  placeholder={p.plan != null ? `e.g. ${nf(Math.round(p.plan * 0.5))}` : ''}
                  autoFocus
                />
                {err.main_value ? <small className="ferr">{err.main_value}</small> : null}
              </label>
              {extras.length ? (
                <>
                  <div className="lbl" style={{ margin: '4px 0 6px' }}>
                    Other numbers in the report
                  </div>
                  <div className="cols c2">
                    {extras.map((x) => (
                      <Field key={x.field_key} label={`${x.label}${x.unit === '%' ? ' (%)' : ''}`} error={err[`extras.${x.field_key}`]}>
                        <input
                          className="in"
                          type="number"
                          min="0"
                          value={xs[x.field_key] ?? ''}
                          onChange={(e) => setXs((m) => ({ ...m, [x.field_key]: e.target.value }))}
                          placeholder={x.unit === '%' ? 'e.g. 62' : ''}
                        />
                      </Field>
                    ))}
                  </div>
                </>
              ) : null}
              <label className="fld">
                <span>
                  Attach the report <span className="sub">(optional, PDF, Excel, CSV or screenshot)</span>
                </span>
                <input type="file" className="in" accept={ATTACHMENT_EXTENSIONS.join(',')} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                {err.file || err.file_key ? <small className="ferr">{err.file || err.file_key}</small> : null}
              </label>
              {p.source === 'api' ? (
                <div className="note warn">This product normally sends its numbers by itself. Type them only to cover a gap while the feed is fixed.</div>
              ) : null}
              {err.form ? <div className="note bad">{err.form}</div> : null}
            </>
          )}
        </>
      )}
    </Modal>
  );
}
